import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useToasts } from 'react-toast-notifications';
import { useTranslation } from 'react-i18next';
import * as ROUTES from '../../constants/routes';
import { blogService } from '../../services/blogService';
import '../../styles/blog.css';

const POSTS_PER_PAGE = 3;

export default function BlogList() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const { addToast } = useToasts();
  const { t, i18n } = useTranslation();
  const forceLtr = i18n.language === 'ar';
  const adRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const fetchPosts = async () => {
      try {
        const { posts, cursor: nextCursor, hasMore: more } =
          await blogService.getPostsPage({ pageSize: POSTS_PER_PAGE });
        if (!isMounted) {
          return;
        }
        setBlogPosts(posts);
        setCursor(nextCursor);
        setHasMore(more);
      } catch (error) {
        if (isMounted) {
          addToast(t('blog.loadError'), {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPosts();
    return () => {
      isMounted = false;
    };
  }, [addToast, t]);

  useEffect(() => {
    // Sadece blog listesi sayfasında reklam göster
    if (window.location.pathname !== '/blog') {
      return;
    }
    
    // Adsense scriptini ekle
    const scriptId = 'adsbygoogle-js';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2743550845530754';
      script.async = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    }
    // Reklamı başlat
    if (window.adsbygoogle && adRef.current) {
      try {
        window.adsbygoogle.push({});
      } catch (e) {}
    }
  }, []);

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const { posts, cursor: nextCursor, hasMore: more } =
        await blogService.getPostsPage({ pageSize: POSTS_PER_PAGE, cursor });
      setBlogPosts(prev => [...prev, ...posts]);
      setCursor(nextCursor);
      setHasMore(more);
    } catch (error) {
      addToast(t('blog.loadMoreError'), {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="container text-center" dir={forceLtr ? 'ltr' : undefined}>
        <div className="spinner-border" role="status">
          <span className="sr-only">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container" dir={forceLtr ? 'ltr' : undefined}>
      {/* Reklam Alanı - Sadece blog listesi sayfasında */}
      {window.location.pathname === '/blog' && (
        <div className="my-3 text-center">
          <ins
            className="adsbygoogle"
            style={{ display: 'block', minHeight: 90 }}
            data-ad-client="ca-pub-2743550845530754"
            data-ad-slot="1234567890"
            data-ad-format="auto"
            data-full-width-responsive="true"
            ref={adRef}
          ></ins>
        </div>
      )}

      {blogPosts.map(post => (
        <div key={post.id} className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                {post.createdAt ? moment(post.createdAt.toDate()).format('DD.MM.YYYY') : t('common.noDate')}
              </small>
              {post.category && (
                <span className="badge badge-primary">{post.category}</span>
              )}
            </div>
            <h2 className="h5 mb-2">
              <Link to={`${ROUTES.BLOG}/${post.id}`} style={{ color: '#0A2342', textDecoration: 'none' }}>
                {post.title}
              </Link>
            </h2>
            <div className="mb-2" dangerouslySetInnerHTML={{ __html: post.excerpt }} />
            <div className="d-flex justify-content-end">
              <Link 
                to={`${ROUTES.BLOG}/${post.id}`} 
                className="btn btn-primary btn-sm"
                onMouseEnter={(e) => { e.target.style.color = '#FFFFFF'; }}
                onMouseLeave={(e) => { e.target.style.color = '#F5F5D1'; }}
                style={{ color: '#F5F5D1' }}
              >
                {t('common.readMore')}
              </Link>
            </div>
          </div>
        </div>
      ))}

      {blogPosts.length === 0 && (
        <div className="alert alert-info">
          {t('blog.noPosts')}
        </div>
      )}

      {hasMore && (
        <div className="text-center my-4">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <span
                className="spinner-grow spinner-grow-sm"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              t('blog.loadMore')
            )}
          </button>
        </div>
      )}
    </div>
  );
} 